import { Fragment, useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { useNavigate, useSearchParams } from '../routing';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import { analysisApi, revenueApi, suppliersApi, formatCurrency } from '../utils/api';
import { buildSupplierSlices, BASIS_NET } from '../utils/supplierSlices';
import { previousRange, changePct } from '../utils/previousRange';
import { formatDateShort } from '../utils/dateFormat';
import lazyWithReload from '../utils/lazyWithReload';
import ClassificationAnalysis from '../components/ClassificationAnalysis';
import WeeklyBudgetBreakdown from '../components/WeeklyBudgetBreakdown';
import RevenueVsBudgetPanel from '../components/RevenueVsBudgetPanel';
import { toast } from '../components/Toast';
import AnalysisFilterBar from '../components/AnalysisFilterBar';
import PriceChangeReportModal from '../components/PriceChangeReportModal';
import TopProductsCard from '../components/TopProductsCard';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { track, EVENTS } from '../utils/analytics';
import { currentMonthRange, periodLabel, PRESETS_ANALYTIC } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import { isoToday } from '../utils/revenueFormat';
import Icon from '../components/Icon';
import ExpandCaret from '../components/ExpandCaret';
import ExportMenu from '../components/ExportMenu';
import HqOnlyNotice from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';

// Loaded on demand: this card is the app's ONLY recharts consumer, and recharts
// (with its d3/reselect deps) is ~84KB gzip — about a third of the entry bundle,
// for one card on one screen. Imported statically it shipped a charting library
// to the login page. Same call, and the same reasoning, as the dynamic
// posthog-js import in utils/analytics.js.
// lazyWithReload, not bare lazy: after a deploy this chunk's old hashed name
// 404s in tabs that were already open, and the flagship card would sit broken
// until a manual reload. The helper does that one reload itself.
const SupplierDonutCard = lazyWithReload(() => import('../components/SupplierDonutCard'));


// ▲/▼ change indicator, shared by the single-restaurant and network views.
function renderChangePct(pct) {
  if (pct === null || pct === undefined) return <span className="change-zero">—</span>;
  const n = parseFloat(pct);
  if (n > 0) return <span className="change-pos"><Icon name="arrow-up" size={12} /> {n}%</span>;
  if (n < 0) return <span className="change-neg"><Icon name="arrow-down" size={12} /> {Math.abs(n)}%</span>;
  return <span className="change-zero">0%</span>;
}

// Consolidated network (chain) report. Self-contained with its own state so it
// can't regress the single-restaurant flow. Three drill levels:
//   merged supplier (by name)  →  per-branch breakdown  →  that branch's products
// Reuses the existing /analysis/supplier/:id endpoint for the deepest level.
// "סניף אחד", never "1 סניפים" — the app's singular form (like "ליום אחד").
function branchesLabel(count) {
  return count === 1 ? 'סניף אחד' : `${count} סניפים`;
}

function NetworkAnalysis({ networkId, dateFrom, dateTo, isAdmin, canViewDiscounts, nameFilter, topProductFilter, onHiddenHq, onExportable }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Bumped by the retry button — this view loads from an effect, so a
  // counter in its deps is what re-runs the fetch.
  const [retry, setRetry] = useState(0);

  // Drill state. expandedName = merged supplier currently open; branchSupplierId
  // = which branch row (real supplier id) is expanded to its product list.
  const [expandedName, setExpandedName] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchSupplierId, setBranchSupplierId] = useState(null);
  const [branchProducts, setBranchProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [branchProdNameFilter, setBranchProdNameFilter] = useState('');
  const [branchProdSkuFilter, setBranchProdSkuFilter] = useState('');

  // Network-pricing tab state (per expanded supplier). tab toggles the
  // expansion between the per-branch breakdown and the network pricing editor.
  const [tab, setTab] = useState('branches');
  const [netProducts, setNetProducts] = useState([]);
  const [supDiscount, setSupDiscount] = useState(null);
  const [supBranchCount, setSupBranchCount] = useState(0);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editForm, setEditForm] = useState({ fixed_price: '', fixed_discount: '', fixed_discount_type: 'percent' });
  const [editingSup, setEditingSup] = useState(false);
  const [supForm, setSupForm] = useState({ value: '', type: 'percent' });

  const params = () => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const resetPricing = () => {
    setTab('branches'); setNetProducts([]); setSupDiscount(null);
    setEditKey(null); setEditingSup(false);
  };

  // Load the merged product list + current network-wide fixed pricing for the
  // open supplier (the 💰 מחירי רשת tab).
  const loadPricing = async (name) => {
    setLoadingPricing(true);
    try {
      const r = await analysisApi.getNetworkProducts(networkId, { ...params(), name });
      setNetProducts(r.data.products || []);
      setSupBranchCount(r.data.supplier_branch_count || 0);
      const sd = r.data.supplier_discount || null;
      setSupDiscount(sd);
      setSupForm({ value: sd && !sd.mixed && sd.value ? sd.value : '', type: sd?.type || 'percent' });
    } catch {
      toast('שגיאה בטעינת מחירי הרשת', 'error');
    } finally {
      setLoadingPricing(false);
    }
  };

  const startEditProduct = (p) => {
    setEditKey(p.group_key);
    setEditForm({
      fixed_price:         p.fixed_price && !p.fixed_price_mixed ? p.fixed_price : '',
      fixed_discount:      p.fixed_discount && !p.fixed_discount_mixed ? p.fixed_discount : '',
      fixed_discount_type: p.fixed_discount_type || 'percent',
    });
  };

  const saveProduct = async (p) => {
    try {
      const r = await analysisApi.setNetworkCatalog(networkId, {
        supplier_name: expandedName,
        sku: p.sku || '',
        name: p.description,
        fixed_price:         editForm.fixed_price === '' ? 0 : editForm.fixed_price,
        fixed_discount:      editForm.fixed_discount === '' ? 0 : editForm.fixed_discount,
        fixed_discount_type: editForm.fixed_discount_type,
      });
      track(EVENTS.NETWORK_PRICING_EDITED, { kind: 'product_price', branches: r.data.branches });
      toast(`הוחל על ${branchesLabel(r.data.branches)}`);
      setEditKey(null);
      await loadPricing(expandedName);
    } catch {
      toast('שגיאה בשמירה', 'error');
    }
  };

  const saveSupDiscount = async () => {
    try {
      const r = await analysisApi.setNetworkSupplierDiscount(networkId, {
        supplier_name: expandedName,
        fixed_invoice_discount:      supForm.value === '' ? 0 : supForm.value,
        fixed_invoice_discount_type: supForm.type,
      });
      track(EVENTS.NETWORK_PRICING_EDITED, { kind: 'supplier_discount', branches: r.data.branches });
      toast(`הנחת ספק הוחלה על ${branchesLabel(r.data.branches)}`);
      setEditingSup(false);
      await loadPricing(expandedName);
    } catch {
      toast('שגיאה בשמירה', 'error');
    }
  };

  // Reset all drill state and refetch when the network or date range changes.
  useEffect(() => {
    setExpandedName(null); setBranches([]); setBranchSupplierId(null); setBranchProducts([]);
    resetPricing();
    setLoading(true);
    setLoadError(false);
    analysisApi.getNetwork(networkId, params())
      .then(r => { setSuppliers(r.data); track(EVENTS.NETWORK_REPORT_VIEWED, { supplier_count: Array.isArray(r.data) ? r.data.length : 0 }); })
      // Was a toast only, which left "אין נתונים לרשת זו" on screen — a
      // chain owner would read that as "this network bought nothing".
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [networkId, dateFrom, dateTo, retry]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSupplier = async (s) => {
    if (expandedName === s.name) {
      setExpandedName(null); setBranches([]); setBranchSupplierId(null); setBranchProducts([]);
      resetPricing();
      return;
    }
    setExpandedName(s.name); setBranchSupplierId(null); setBranchProducts([]);
    resetPricing();
    track(EVENTS.NETWORK_DRILLED, { level: 'supplier' });
    setLoadingBranches(true);
    try {
      const r = await analysisApi.getNetworkBreakdown(networkId, { ...params(), name: s.name });
      setBranches(r.data);
    } catch {
      toast('שגיאה בטעינת פירוט הסניפים', 'error');
    } finally {
      setLoadingBranches(false);
    }
  };

  const toggleBranch = async (b) => {
    setBranchProdNameFilter(''); setBranchProdSkuFilter('');
    if (branchSupplierId === b.supplier_id) {
      setBranchSupplierId(null); setBranchProducts([]);
      return;
    }
    setBranchSupplierId(b.supplier_id); setBranchProducts([]);
    track(EVENTS.NETWORK_DRILLED, { level: 'branch' });
    setLoadingProducts(true);
    try {
      const r = await analysisApi.getSupplier(b.supplier_id, params());
      setBranchProducts(r.data);
    } catch {
      toast('שגיאה בטעינת מוצרי הסניף', 'error');
    } finally {
      setLoadingProducts(false);
    }
  };

  // "סניפים שקנו" cell — stays compact no matter how many branches: an N/M
  // badge plus the first couple of names + "ועוד X", full list on hover.
  const branchesCell = (p) => {
    const names = (p.branch_names || '').split(',').filter(Boolean);
    const shown = names.slice(0, 2).join(', ');
    const more = names.length > 2 ? ` ועוד ${names.length - 2}` : '';
    return (
      <td style={{ fontSize: '0.8rem' }}>
        <span title={p.branch_names || ''} style={{ background: '#e7eefb', border: '1px solid #c5d5e8', borderRadius: 4, padding: '1px 6px', fontWeight: 600, color: '#1a4b8c', whiteSpace: 'nowrap' }}>
          {p.branch_count}/{supBranchCount}
        </span>
        <div title={p.branch_names || ''} style={{ color: 'var(--ink-muted)', fontSize: '0.72rem', marginTop: 2, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {shown}{more}
        </div>
      </td>
    );
  };

  const _nameQ = (nameFilter || '').trim();
  const _prodQ = (topProductFilter || '').trim().toLowerCase();
  const filtered = suppliers.filter(s => {
    if (_nameQ && !s.name.includes(_nameQ)) return false;
    if (_prodQ) {
      const hit = (s.products || []).some(p =>
        (p.readable_name || '').toLowerCase().includes(_prodQ) ||
        (p.description   || '').toLowerCase().includes(_prodQ)
      );
      if (!hit) return false;
    }
    return true;
  });

  // Owner indicator for the chain view: the hidden part of the rows ON SCREEN
  // (same members, period and name/product filters as the table), summed from
  // the server's per-row hidden_hq_amount. Withheld while a refetch is pending.
  const hiddenSum = Math.round(filtered.reduce((n, s) => n + (s.hidden_hq_amount || 0), 0) * 100) / 100;
  useEffect(() => {
    onHiddenHq?.({ amount: loadError ? null : hiddenSum, pending: loading });
  }, [hiddenSum, loading, loadError]); // eslint-disable-line react-hooks/exhaustive-deps
  // The chain export is a page-level action, so the page's title row owns the
  // button; this view reports the merged supplier names on screen (null when
  // there is nothing to export). Keyed on a joined string: `filtered` is a new
  // array every render, and a setState per render would loop.
  const exportKey = !loading && !loadError && filtered.length > 0
    ? filtered.map(s => s.name).join('\u0001') : '';
  useEffect(() => {
    onExportable?.(exportKey ? exportKey.split('\u0001') : null);
  }, [exportKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="card">
      <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)', margin: '0 0 16px' }}>סיכום מאוחד לפי ספק (כל הרשת)</h3>

      {loading ? (
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      ) : loadError ? (
        <InlineLoadError message="לא ניתן לטעון את דוח הרשת" onRetry={() => setRetry(n => n + 1)} />
      ) : filtered.length === 0 ? (
        <EmptyState message="אין נתונים לרשת זו בתקופה שנבחרה" />
      ) : (
        <div className="table-wrapper table-scroll-mobile">
          <table>
            <thead>
              <tr>
                <th>ספק</th>
                <th>סניפים</th>
                <th>כמות מוצרים</th>
                <th>סכום כולל</th>
                <th>מחיר מינימום</th>
                <th>מחיר מקסימום</th>
                <th>מחיר נוכחי</th>
                <th>% שינוי</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <Fragment key={s.id}>
                  <tr className="row-clickable" onClick={() => toggleSupplier(s)}
                    style={{ background: expandedName === s.name ? '#eaf0f8' : undefined }}>
                    <td style={{ fontWeight: 600 }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <ExpandCaret open={expandedName === s.name} />{s.name}
                      </span>
                    </td>
                    <td>
                      {/* nowrap like the N/M badge in the product rows: at phone
                          width it broke onto two lines and its border clipped. */}
                      <span style={{ fontSize: '0.78rem', color: '#1a4b8c', background: '#e7eefb', border: '1px solid #c5d5e8', borderRadius: 4, padding: '1px 7px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {branchesLabel(s.branch_count)}
                      </span>
                    </td>
                    <td>{s.product_count}</td>
                    <td>{formatCurrency(s.total_amount)}</td>
                    <td>{formatCurrency(s.min_price)}</td>
                    <td>{formatCurrency(s.max_price)}</td>
                    <td>{formatCurrency(s.current_price)}</td>
                    <td>{renderChangePct(s.change_pct)}</td>
                  </tr>
                  {expandedName === s.name && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0, background: '#fafbfc', borderTop: '1px solid #e5ebf5' }}>
                        <div style={{ padding: '16px 20px' }}>
                          {/* Pricing tab: admins (full edit) + approved viewers
                              (can_view_discounts, read-only). Others see just the
                              consolidated report with no tab bar. */}
                          {canViewDiscounts && (
                            <div style={{ display: 'flex', gap: 8, marginBottom: 14, borderBottom: '1px solid #e5ebf5' }}>
                              <button
                                className="btn btn-sm"
                                style={{ background: tab === 'branches' ? 'var(--selected-solid-bg)' : 'transparent', color: tab === 'branches' ? '#fff' : 'var(--ink-muted)', border: 'none', borderRadius: '6px 6px 0 0' }}
                                onClick={() => setTab('branches')}
                              ><Icon name="analysis" size={15} /> פירוט סניפים</button>
                              <button
                                className="btn btn-sm"
                                style={{ background: tab === 'pricing' ? 'var(--selected-solid-bg)' : 'transparent', color: tab === 'pricing' ? '#fff' : 'var(--ink-muted)', border: 'none', borderRadius: '6px 6px 0 0' }}
                                onClick={() => { setTab('pricing'); loadPricing(s.name); }}
                              ><Icon name="coins" size={15} /> מחירי רשת</button>
                            </div>
                          )}

                          {(!canViewDiscounts || tab === 'branches') ? (
                          <>
                          {(() => {
                            const disc = canViewDiscounts ? (s.invoice_discount_total || 0) : 0;
                            const pdc  = s.price_diff_credit_total || 0;
                            if (disc === 0 && pdc === 0) return null;
                            return (
                              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center',
                                background: '#f0f9f4', border: '1px solid #86c9a4', borderRadius: 8,
                                padding: '10px 16px', marginBottom: 14 }}>
                                <span>סכום מוצרים: <b>{formatCurrency(s.total_amount + disc + pdc)}</b></span>
                                {pdc !== 0 && (
                                  <span style={{ color: '#c0392b' }}>זיכוי הפרש מחיר: <b>{formatCurrency(-pdc)}</b></span>
                                )}
                                {disc !== 0 && (
                                  <span style={{ color: '#c0392b' }}>הנחת חשבונית: <b>{formatCurrency(-disc)}</b></span>
                                )}
                                <span style={{ fontWeight: 700 }}>סכום כולל: {formatCurrency(s.total_amount)}</span>
                              </div>
                            );
                          })()}
                          <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 12px' }}>
                            פירוט לפי סניף — {s.name}
                          </h4>
                          {loadingBranches ? (
                            <div className="loading-container"><div className="spinner" /></div>
                          ) : branches.length === 0 ? (
                            <div className="empty-state"><p>אין נתונים</p></div>
                          ) : (
                            <div className="table-wrapper">
                              <table>
                                <thead>
                                  <tr><th>סניף</th><th>כמות מוצרים</th><th>סכום כולל</th></tr>
                                </thead>
                                <tbody>
                                  {branches.map(b => (
                                    <Fragment key={b.supplier_id}>
                                      <tr className="row-clickable" onClick={() => toggleBranch(b)}
                                        style={{ background: branchSupplierId === b.supplier_id ? '#eaf0f8' : undefined }}>
                                        <td style={{ fontWeight: 600 }}>
                                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            <ExpandCaret open={branchSupplierId === b.supplier_id} />{b.restaurant_name}
                                          </span>
                                        </td>
                                        <td>{b.product_count}</td>
                                        <td>{formatCurrency(b.total_amount)}</td>
                                      </tr>
                                      {branchSupplierId === b.supplier_id && (
                                        <tr>
                                          <td colSpan={3} style={{ padding: 0, background: '#f4f7fb', borderTop: '1px solid #d8e1ee' }}>
                                            <div style={{ padding: '14px 18px' }}>
                                              <h5 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 10px' }}>
                                                מוצרי {b.restaurant_name}
                                              </h5>
                                              {loadingProducts ? (
                                                <div className="loading-container"><div className="spinner" /></div>
                                              ) : branchProducts.length === 0 ? (
                                                <div className="empty-state"><p>אין נתוני מחירים</p></div>
                                              ) : (
                                                <>
                                                <div className="filter-bar" style={{ marginBottom: 12 }}>
                                                  <div className="filter-group">
                                                    <label>שם מוצר</label>
                                                    <input className="filter-input" placeholder="חיפוש לפי שם מוצר..."
                                                      value={branchProdNameFilter}
                                                      onChange={e => setBranchProdNameFilter(e.target.value)}
                                                      style={{ width: 200 }} />
                                                  </div>
                                                  <div className="filter-group">
                                                    <label>מק"ט</label>
                                                    <input className="filter-input" placeholder='חיפוש לפי מק"ט...'
                                                      value={branchProdSkuFilter}
                                                      onChange={e => setBranchProdSkuFilter(e.target.value)}
                                                      style={{ width: 150 }} />
                                                  </div>
                                                  {(branchProdNameFilter || branchProdSkuFilter) && (
                                                    <button className="btn btn-secondary btn-sm"
                                                      onClick={() => { setBranchProdNameFilter(''); setBranchProdSkuFilter(''); }}>
                                                      <Icon name="close" size={14} /> נקה
                                                    </button>
                                                  )}
                                                </div>
                                                <div className="table-wrapper table-scroll-mobile">
                                                  <table>
                                                    <thead>
                                                      <tr>
                                                        <th>מק"ט</th><th>תיאור מוצר</th><th>כמות כוללת</th>
                                                        <th>סכום כולל</th><th>מחיר ממוצע</th><th>מחיר נוכחי</th><th>מחיר קבוע</th><th>% שינוי</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {branchProducts.filter(p => {
                                                        const nq = branchProdNameFilter.trim().toLowerCase();
                                                        const sq = branchProdSkuFilter.trim().toLowerCase();
                                                        if (nq && !(p.description || '').toLowerCase().includes(nq)) return false;
                                                        if (sq && !(p.sku || '').toLowerCase().includes(sq)) return false;
                                                        return true;
                                                      }).map((p, pi) => (
                                                        <tr key={pi}>
                                                          <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{p.sku || '—'}</td>
                                                          <td style={{ fontWeight: 500 }}>{p.description}</td>
                                                          <td>{p.total_qty}</td>
                                                          <td>{formatCurrency(p.total_amount)}</td>
                                                          <td>{formatCurrency(p.avg_price)}</td>
                                                          <td style={{ fontWeight: 600 }}>{formatCurrency(p.current_price)}</td>
                                                          <td>
                                                            {(p.fixed_price || 0) > 0
                                                              ? <span style={{ fontSize: '0.74rem', color: '#9a3412', background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 4, padding: '1px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatCurrency(p.fixed_price)}</span>
                                                              : <span style={{ color: '#bbb' }}>—</span>}
                                                          </td>
                                                          <td>{renderChangePct(p.change_pct)}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </div>
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          </>
                          ) : (
                          <>
                          <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 4px' }}>
                            מחירי רשת — {s.name}
                          </h4>
                          <p style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', margin: '0 0 14px' }}>
                            {s.branch_count === 1
                              ? 'כל הגדרה כאן נכתבת לסניף אחד ברשת.'
                              : `כל הגדרה כאן נכתבת ל-${s.branch_count} סניפי הרשת בבת אחת.`}
                          </p>

                          {/* Supplier-level invoice discount */}
                          <div style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: '#9a3412' }}>הנחת חשבונית קבועה לספק:</span>
                            {editingSup && isAdmin ? (
                              <>
                                <input type="number" value={supForm.value} placeholder="—"
                                  onChange={e => setSupForm(f => ({ ...f, value: e.target.value }))}
                                  style={{ border: '1px solid #fdba74', borderRadius: 6, padding: '4px 8px', width: 90, background: '#fff' }} />
                                <select value={supForm.type} onChange={e => setSupForm(f => ({ ...f, type: e.target.value }))}
                                  style={{ border: '1px solid #fdba74', borderRadius: 6, padding: '4px 8px', background: '#fff' }}>
                                  <option value="percent">%</option>
                                  <option value="amount">₪</option>
                                </select>
                                <button className="btn btn-sm btn-primary" onClick={saveSupDiscount}>החל על כל הסניפים</button>
                                <button className="btn btn-sm btn-secondary" onClick={() => setEditingSup(false)}>ביטול</button>
                              </>
                            ) : (
                              <>
                                <span style={{ fontWeight: 700 }}>
                                  {supDiscount?.mixed
                                    ? <span style={{ color: '#b45309' }}>מעורב בין סניפים</span>
                                    : (supDiscount?.value
                                        ? (supDiscount.type === 'amount' ? formatCurrency(supDiscount.value) : `${supDiscount.value}%`)
                                        : 'אין')}
                                </span>
                                {isAdmin && (
                                  <button className="btn btn-sm btn-secondary" onClick={() => setEditingSup(true)}>ערוך</button>
                                )}
                              </>
                            )}
                          </div>

                          {/* Per-product fixed price + discount */}
                          {loadingPricing ? (
                            <div className="loading-container"><div className="spinner" /></div>
                          ) : netProducts.length === 0 ? (
                            <div className="empty-state"><p>אין מוצרים</p></div>
                          ) : (
                            <div className="table-wrapper table-scroll-mobile">
                              <table>
                                <thead>
                                  <tr>
                                    <th>מק"ט</th><th>תיאור מוצר</th><th>כמות</th><th>סניפים שקנו</th><th>מחיר ממוצע</th>
                                    <th>מחיר קבוע (רשת)</th><th>הנחה קבועה (רשת)</th>{isAdmin && <th>פעולות</th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {netProducts.map(p => editKey === p.group_key ? (
                                    <tr key={p.group_key} style={{ background: '#fffbe6' }}>
                                      <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{p.sku || '—'}</td>
                                      <td style={{ fontWeight: 500 }}>{p.description}</td>
                                      <td>{p.total_qty}</td>
                                      {branchesCell(p)}
                                      <td>{formatCurrency(p.avg_price)}</td>
                                      <td>
                                        <input type="number" value={editForm.fixed_price} placeholder="—"
                                          onChange={e => setEditForm(f => ({ ...f, fixed_price: e.target.value }))}
                                          style={{ border: '1px solid #fdba74', borderRadius: 6, padding: '4px 8px', width: 90, background: '#fff7ed' }} />
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                          <input type="number" value={editForm.fixed_discount} placeholder="—"
                                            onChange={e => setEditForm(f => ({ ...f, fixed_discount: e.target.value }))}
                                            style={{ border: '1px solid #fdba74', borderRadius: 6, padding: '4px 8px', width: 64, background: '#fff7ed' }} />
                                          <select value={editForm.fixed_discount_type} onChange={e => setEditForm(f => ({ ...f, fixed_discount_type: e.target.value }))}
                                            style={{ border: '1px solid #fdba74', borderRadius: 6, padding: '4px 8px', background: '#fff7ed' }}>
                                            <option value="percent">%</option>
                                            <option value="amount">₪</option>
                                          </select>
                                        </div>
                                      </td>
                                      <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                          <button className="btn btn-sm btn-primary" onClick={() => saveProduct(p)}>החל</button>
                                          <button className="btn btn-sm btn-secondary" onClick={() => setEditKey(null)}>ביטול</button>
                                        </div>
                                      </td>
                                    </tr>
                                  ) : (
                                    <tr key={p.group_key}>
                                      <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{p.sku || '—'}</td>
                                      <td style={{ fontWeight: 500 }}>{p.description}</td>
                                      <td>{p.total_qty}</td>
                                      {branchesCell(p)}
                                      <td>{formatCurrency(p.avg_price)}</td>
                                      <td>{p.fixed_price_mixed
                                        ? <span style={{ color: '#b45309' }}>מעורב</span>
                                        : (p.fixed_price ? formatCurrency(p.fixed_price) : '—')}</td>
                                      <td>{p.fixed_discount_mixed
                                        ? <span style={{ color: '#b45309' }}>מעורב</span>
                                        : (p.fixed_discount ? (p.fixed_discount_type === 'amount' ? formatCurrency(p.fixed_discount) : `${p.fixed_discount}%`) : '—')}</td>
                                      {isAdmin && <td><button className="btn btn-sm btn-secondary" onClick={() => startEditProduct(p)}>ערוך</button></td>}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#eaf0f8', fontWeight: 700, borderTop: '2px solid #c5d5e8' }}>
                <td style={{ padding: '10px 16px', color: '#1a1a2e' }} colSpan={2}>סה"כ</td>
                <td style={{ padding: '10px 16px' }}>{filtered.reduce((a, x) => a + (x.product_count || 0), 0)}</td>
                <td style={{ padding: '10px 16px' }}>{formatCurrency(filtered.reduce((a, x) => a + (x.total_amount || 0), 0))}</td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default function AnalysisPage() {
  const { canViewDiscounts, canEdit, restaurants, restaurantId, isAdmin, realRole, canSeeHqOnly } = useAuth();
  // The active restaurant, named the same way the navbar chip names it — the
  // subtitle under the scope pill echoes it verbatim in single view, which is
  // what teaches the relationship between the two controls without a word of
  // explanation. AuthContext keeps restaurantNameFor private, so derive it from
  // the list it does expose.
  const activeRestaurantName = restaurants?.find(r => String(r.id) === String(restaurantId))?.name;
  // Gates the two budget widgets below. Defaults false pre-fetch, so a
  // module-off restaurant never sees them flash during load.
  const { budgetModuleEnabled, topProductsEnabled } = useSettings();
  const navigate = useNavigate();
  // Keep the sticky table header docked below this page's sticky filter bar.

  // Network (chain) consolidated-report mode. selectedNetworkId null = normal
  // single-restaurant view; a value swaps in <NetworkAnalysis>.
  //
  // The chain lives in the URL (`?network=<id>`, #184) so the view survives a
  // refresh and can be sent as a link. The restaurant stays in the path: a
  // chain is a view on top of a branch, not a tenant (networkScope.js). The
  // param is only a REQUEST — the selection is whichever chain of THIS
  // restaurant it names, so a stale or foreign id selects nothing, and is
  // dropped from the URL once the list has actually loaded.
  const [searchParams, setSearchParams] = useSearchParams();
  const networkParam = searchParams.get('network');
  const [networks, setNetworks] = useState([]);
  // 'loading' | 'loaded' | 'failed'. A failed list keeps the param: the link
  // is not known to be wrong, and a retry (refresh) can still honour it.
  const [networksStatus, setNetworksStatus] = useState('loading');
  const selectedNetworkId = networks.find(n => String(n.id) === networkParam)?.id ?? null;
  // A chain link we cannot confirm yet — the list is in flight, or it FAILED.
  // Either way the page must not stand in for the chain with the single
  // restaurant: no branch figures in the body (a spinner, or an error with a
  // retry) and no branch-scoped header actions, since an export from here
  // would hand over the branch's workbook under a chain URL (Codex, #193).
  const [networksRetry, setNetworksRetry] = useState(0);
  const networkPending = networkParam != null && networksStatus === 'loading';
  const chainUnresolved = networkParam != null && networksStatus !== 'loaded';
  // Owner indicator in the chain view (Codex review, PR #173): the count is the
  // chain's (every member branch this user sees), never the current branch's,
  // and the amount comes from NetworkAnalysis's own rows.
  const [networkHqCount, setNetworkHqCount] = useState(0);
  const [networkHidden, setNetworkHidden] = useState({ amount: null, pending: true });
  // The chain view's on-screen supplier names (reported by NetworkAnalysis;
  // null = nothing to export). The ייצוא button itself lives in the title row.
  const [networkExportNames, setNetworkExportNames] = useState(null);
  useEffect(() => {
    setNetworkHqCount(0);
    setNetworkHidden({ amount: null, pending: true });
    setNetworkExportNames(null);
    if (!selectedNetworkId || !canSeeHqOnly) return undefined;
    let alive = true;
    suppliersApi.hqOnlySummary({ network_id: selectedNetworkId })
      .then(r => { if (alive) setNetworkHqCount(r.data?.supplier_count || 0); })
      .catch(err => {
        console.error('network hq-only-summary failed', err);
        if (alive) toast('לא הצלחנו לבדוק אם יש ספקים מוסתרים מהצוות ברשת — כדאי לרענן את הדף', 'error');
      });
    return () => { alive = false; };
  }, [selectedNetworkId, canSeeHqOnly]);

  // Single-restaurant sub-view toggle: 'supplier' (default by-supplier table)
  // vs 'classification' (by-classification analytics). Only meaningful when no network is
  // selected; the network view ignores it.

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // The search as typed. Read it through nameFilter / topProductFilter below,
  // never directly.
  const [nameQuery, setNameFilter] = useState('');
  const [productQuery, setTopProductFilter] = useState('');

  // The single-restaurant view MUST NOT apply the supplier/product search: it
  // hides those fields (hideSupplierFilters), so anything still applied there
  // narrows every figure on the page — סה״כ רכש, the donut, and the exports,
  // which post filtered.map(s => s.id) — with no field, no chip and nothing on
  // screen to explain it. Measured on real data: a name filter typed in the
  // network view survived the trip back and reported August as ₪27,644 instead
  // of ₪207,205. An active filter nobody can see is the worst kind of wrong
  // number.
  //
  // So it is enforced where the filters are READ, not by clearing them in
  // time: since the chain moved into the URL (#184) the scope can change
  // without the switch (a navbar link to /analysis drops `?network=` without a
  // remount; an unknown id is dropped after load), and an effect clears only
  // AFTER the render that already applied the old term.
  const nameFilter = selectedNetworkId ? nameQuery : '';
  const topProductFilter = selectedNetworkId ? productQuery : '';
  // …and the typed term is still cleared on every scope change, so returning
  // to the chain (or to another chain) never brings back an old search.
  useEffect(() => {
    setNameFilter('');
    setTopProductFilter('');
  }, [selectedNetworkId]);
  // `replace`, like the report pages' period params: a scope switch is not a
  // page the back button should step through.
  const setScope = (networkId) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (networkId == null) next.delete('network');
      else next.set('network', String(networkId));
      return next;
    }, { replace: true });
  };
  useEffect(() => {
    if (networksStatus !== 'loaded' || networkParam == null || selectedNetworkId != null) return;
    setScope(null);
  }, [networksStatus, networkParam, selectedNetworkId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [reportOpen, setReportOpen] = useState(false);
  // Default the date filter to the current calendar month so the page lands
  // on "this month" data on first render.
  const [dateFrom, setDateFrom] = useState(currentMonthRange().from);
  const [dateTo, setDateTo] = useState(currentMonthRange().to);
  // Rolls a preset forward when the tab comes back after the day changed — the
  // cards' report links carry this range as `?period=` (usePresetPeriod).
  usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, (f, t) => { setDateFrom(f); setDateTo(t); });
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);

  // Fire once when the procurement-analysis (ניתוח רכש) screen is opened.
  useEffect(() => {
    track(EVENTS.ANALYSIS_VIEWED, { role: realRole });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The by-classification view used to be a tab you switched into, and that switch
  // was worth tracking. It now renders alongside the supplier card on every
  // load, so a "viewed classification" event would fire for everyone every time and
  // measure nothing. Dropped rather than left to log noise.

  const buildParams = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  // The same params PLUS the supplier filter, resolved to ids exactly the way
  // the exports below already do it.
  //
  // The by-supplier card has always honoured the name/product filter (it is
  // handed `filtered`), while the by-classification card received dates only — so
  // typing a supplier name narrowed one card and silently left the other
  // describing every supplier in the period. Two cards under one filter bar,
  // two different populations, and nothing on screen admitting it.
  const buildScopedParams = () => ({
    ...buildParams(),
    ...(nameFilter || topProductFilter
      ? { supplier_ids: filtered.map(s => s.id).join(',') } : {}),
  });

  // A generation counter, not the `let cancelled` flag its sibling effects use.
  // Those guard ONE effect; this fetch has four callers — the date effect, the ⋮
  // רענן action, the focus/visibility listener and the donut's retry button —
  // so the guard has to live with the function, not with any one caller.
  //
  // Without it a slow response could land after a newer one and repaint the
  // page with another period's suppliers: the supplier list, the donut, סה״כ רכש
  // and the exports (which post filtered.map(s => s.id)) all read `suppliers`,
  // while the header kept showing the range the user actually picked. Clicking
  // two months in the date popover is enough to start two in flight.
  const reqIdRef = useRef(0);
  const fetchSuppliers = async () => {
    const reqId = ++reqIdRef.current;
    const current = () => reqId === reqIdRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await analysisApi.getAll(buildParams());
      if (current()) setSuppliers(r.data);
    } catch {
      // A failed fetch must never fall through to the empty state — on a
      // spend-analysis screen "אין נתונים לניתוח" reads as "you bought
      // nothing this period" (see InlineLoadError). A SUPERSEDED failure is
      // not an error to report either: the newer request owns the screen.
      if (current()) setLoadError(true);
    } finally {
      // Only the newest request may clear the spinner — an older one finishing
      // second would otherwise declare the page loaded while it is still
      // fetching.
      if (current()) setLoading(false);
    }
  };

  // Re-sync against fresh DB state — used by the date-change effect, the ⋮
  // "רענן" action, and the focus listener (a doc edited in another tab leaves
  // the analysis SQL's live prices ahead of this page's frozen state).
  // A failed chain list is retried too: on a chain link it is what the page
  // is waiting for, and the ⋮ רענן would otherwise refresh only the branch.
  const refreshAll = () => {
    fetchSuppliers();
    if (networksStatus === 'failed') setNetworksRetry(n => n + 1);
  };

  useEffect(() => {
    fetchSuppliers();
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // The chains the CURRENTLY SELECTED restaurant belongs to — not the chains
  // this user could reach in general. Drives the scope switch.
  //
  // Keyed on restaurantId, NOT []. A mount-only [] would also work today, but
  // only by accident of another file: App.js renders <main key={restaurantId}>,
  // so the navbar chip remounts this page and re-runs a mount effect. That
  // guarantee is real and deliberate — but it is not this page's, and it
  // ALREADY has an exception, since the Command Center shell renders without
  // the key on purpose. Depending on the restaurant this page actually reads is
  // cheaper than depending on a remount it does not control.
  //
  // The supplier/product search is cleared for the same reason setScope clears
  // it: single-restaurant view HIDES those fields (hideSupplierFilters) while
  // `filtered` goes on applying them, so a term naming the previous tenant's
  // suppliers would silently narrow every figure with nothing on screen to
  // explain it. Same bug, the other way into the state.
  //
  // Emptying the list is also what drops a chain selected under the previous
  // restaurant: the selection is derived from this list, and the param is
  // removed once the new one loads without that chain.
  useEffect(() => {
    setNameFilter('');
    setTopProductFilter('');
    setNetworks([]);
    setNetworksStatus('loading');
    let cancelled = false;
    analysisApi.getMyNetworks()
      .then(r => {
        if (cancelled) return;
        setNetworks(Array.isArray(r.data) ? r.data : []);
        setNetworksStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setNetworks([]);
        setNetworksStatus('failed');
      });
    return () => { cancelled = true; };
  }, [restaurantId, networksRetry]);

  // When the user comes back to this tab (focus or visibility change), pull
  // fresh data — the analysis SQL reads i.unit_price live from the items
  // table, but the React state up here is otherwise frozen until the user
  // interacts.
  //
  // Chrome fires visibilitychange AND focus on a normal tab re-selection, so
  // both listeners ran the refetch for one return — a duplicate query against a
  // SYNCHRONOUS better-sqlite3 connection, blocking every other request twice
  // over. Both events stay bound (neither alone covers every path back); the
  // timestamp guard collapses the pair. Kept identical to the copy in
  // SupplierPurchaseReportPage — the two are the same behaviour, and fixing one
  // alone is how they start to drift.
  const lastRefreshRef = useRef(0);
  useEffect(() => {
    const onFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastRefreshRef.current < 2000) return;
      lastRefreshRef.current = now;
      refreshAll();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per FILTER_AND_DATE_RULES.md §6: "Clear all" returns to defaults, not to
  // empty (= "all time"). Empty is reachable via the date popover's "נקה טווח"
  // button; there is no "כל הזמן" preset.
  const clearFilters = () => {
    const fresh = currentMonthRange();
    setNameFilter('');
    setTopProductFilter('');
    setDateFrom(fresh.from);
    setDateTo(fresh.to);
  };

  const _topProductQ = topProductFilter.trim().toLowerCase();
  // Memoised so the array IDENTITY is stable between renders. SupplierDonutCard
  // memoises its ranking on this prop; a fresh array every render would defeat
  // that memo and re-sort the whole supplier list on every render — including
  // every mousemove across the donut, which sets tooltip state.
  const filtered = useMemo(() => suppliers.filter(s => {
    if (nameFilter && !s.name.includes(nameFilter)) return false;
    if (_topProductQ) {
      // Match on the real product name (readable_name) OR the SKU-or-name key
      // (description). The list endpoint keys products by SKU when present,
      // so without readable_name a user typing the Hebrew name would miss
      // every SKU-tagged product.
      const hit = (s.products || []).some(p =>
        (p.display_name  || '').toLowerCase().includes(_topProductQ) ||
        (p.readable_name || '').toLowerCase().includes(_topProductQ) ||
        (p.description   || '').toLowerCase().includes(_topProductQ)
      );
      if (!hit) return false;
    }
    return true;
  }), [suppliers, nameFilter, _topProductQ]);

  const hasFilters = nameFilter || topProductFilter || dateFrom || dateTo;

  // ── the headline metric, above the by-supplier card ──────────────────────
  //
  // Taken from buildSupplierSlices rather than a reduce of its own, and from
  // the same `filtered` array the donut is handed, so the strip and the card
  // beneath it are the SAME number by construction. Two figures labelled
  // סה״כ רכש on one screen must not be able to disagree — and they nearly
  // would: grandTotal counts only suppliers with positive spend (a donut
  // cannot draw a negative slice), which a plain sum would not have done.
  const spend = useMemo(() => ({
    net: buildSupplierSlices(filtered, { basis: BASIS_NET }).total,
    // Owner indicator: the same figure as staff see it, through the same
    // slicer (positive spenders only), so the gap is exactly the difference
    // between the owner's סה״כ רכש and the manager's.
    staffNet: buildSupplierSlices(filtered.filter(s => !s.hq_only), { basis: BASIS_NET }).total,
  }), [filtered]);

  // A range that runs to today or past it has not finished arriving. Invoices
  // for the last few days are still being entered and approved, so the current
  // figure is understated against ANY earlier window — and unlike the elapsed-
  // days truncation previousRange already does, no arithmetic fixes it: the
  // documents simply are not in the database yet. The owner sees the current
  // month reported as a fall every time. So an open range trades the delta for
  // the reason there isn't one.
  const rangeIsOpen = Boolean(dateTo) && dateTo >= isoToday();

  // Exactly one call either way: a closed range asks about the PREVIOUS window
  // (for the delta), an open one asks about ITSELF (for the pending count).
  // Its own light endpoint — re-running GET / would also run a price-history
  // query per supplier that this single number has no use for.
  const [prevSpend, setPrevSpend] = useState(null);
  const [openInfo, setOpenInfo] = useState(null);
  const [totalMenuOpen, setTotalMenuOpen] = useState(false);
  // The delta names "the previous period" without ever saying which dates that
  // is. On desktop a native title answered it; on a phone there is no hover, so
  // the one place the comparison could be checked was unreachable exactly where
  // this page is mostly read.
  const [prevTipOpen, setPrevTipOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (rangeIsOpen) {
      setPrevSpend(null);
      analysisApi.getTotal({ date_from: dateFrom, date_to: dateTo })
        .then(r => { if (!cancelled) setOpenInfo(r.data); })
        .catch(() => { if (!cancelled) setOpenInfo(null); });
      return () => { cancelled = true; };
    }
    setOpenInfo(null);
    const prev = previousRange(dateFrom, dateTo);
    if (!prev) { setPrevSpend(null); return undefined; }
    analysisApi.getTotal({ date_from: prev.from, date_to: prev.to })
      .then(r => { if (!cancelled) setPrevSpend(r.data); })
      // No delta beats a wrong one: the strip simply shows the figure alone.
      .catch(() => { if (!cancelled) setPrevSpend(null); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, rangeIsOpen]);

  // A name/product filter narrows the CURRENT figure but not the previous one
  // — that endpoint knows nothing about it — so comparing them would measure
  // the filter, not the spend. The figure still shows; only the delta stands
  // down, which is the half that would have been a lie.
  //
  // NET vs net (owner): the card shows only the without-VAT figure now, so the
  // delta must judge that same figure against the previous period's net total
  // — not the gross one, which would silently compare two different numbers.
  const deltaPct = (nameFilter || topProductFilter || rangeIsOpen)
    ? null
    : changePct(spend.net, prevSpend?.total_amount);

  return (
    <div>
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">ניתוח רכש</h1>
          {chainUnresolved ? null : selectedNetworkId
            ? <HqOnlyNotice count={networkHqCount} amount={networkHidden.amount} pending={networkHidden.pending} basis="סכום כולל ברשת בתקופה (ללא מע״מ)" />
            : <HqOnlyNotice amount={loadError ? null : spend.net - spend.staffNet} pending={loading} basis="רכש בתקופה (ללא מע״מ)" />}
        </div>
        {/* The export control and the ⋮ live in ONE flex group so the row's
            space-between puts the pair at the edge instead of scattering the
            export button into the middle of the header. In RTL the ExportMenu,
            as first child, sits immediately to the ⋮'s right. */}
        <div className="page-title-actions">
        {/* One export control for everything the page can export without a
            selection. The per-card buttons are gone. The network view
            exports a different report (the chain's merged suppliers), so it
            gets its own ייצוא here in the same slot.

            When the supplier-name or product filter is active, the client
            passes the FILTERED SUPPLIER IDS instead of re-describing the
            filter: the export is then exactly the rows on screen, and can
            never drift from however the client-side filtering evolves. */}
        {selectedNetworkId || chainUnresolved ? (
          <button className="btn btn-sm btn-excel"
            disabled={chainUnresolved || !networkExportNames}
            title="ייצוא הסיכום המאוחד של הרשת לאקסל"
            onClick={() => {
              track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'network' });
              // Same dates NetworkAnalysis's own fetch sends (an empty one is
              // omitted). With a supplier/product filter on, also the names on
              // screen, so the file holds exactly the rows the user sees —
              // like the single view's supplier_ids.
              analysisApi.exportNetwork(selectedNetworkId, {
                ...(dateFrom ? { date_from: dateFrom } : {}),
                ...(dateTo ? { date_to: dateTo } : {}),
              }, nameFilter || topProductFilter ? networkExportNames : undefined);
            }}>
            <Icon name="spreadsheet" size={14} />
            ייצוא
          </button>
        ) : (
          <ExportMenu
            scopeLabel={periodLabel(dateFrom, dateTo)}
            options={[
              {
                key: 'suppliers',
                label: 'רכש לפי ספק',
                hint: 'שורה לכל ספק: סכום, מחיר נוכחי ואחוז שינוי',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'suppliers' });
                  return analysisApi.exportAll({
                    ...buildParams(),
                    ...(nameFilter || topProductFilter
                      ? { supplier_ids: filtered.map(s => s.id).join(',') } : {}),
                  });
                },
              },
              // Same workbook the report page's own דוחות button produces, and
              // reachable from here for the same reason the card is: the owner
              // is already looking at the period they want. Gated on the module
              // flag exactly like the card is — the route answers 403 without
              // it, and an export that can only fail is worse than one that is
              // not offered.
              ...(budgetModuleEnabled ? [{
                key: 'revenue',
                label: 'דוח הכנסות מול הוצאות',
                hint: 'קטגוריות, פירוק חודשי ופירוק לפי ספקים',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'revenue_report' });
                  // Dates only: this report has no supplier or product axis to
                  // narrow, so passing the page's filters would silently
                  // describe a scope the workbook does not honour.
                  return revenueApi.exportReport({ date_from: dateFrom, date_to: dateTo });
                },
              }] : []),
              // The top-products report's own file, gated on its flag like the
              // card (the route answers 403 without it). Same params as the
              // card, so the file holds the money the card shows.
              ...(topProductsEnabled ? [{
                key: 'top_products',
                label: 'המוצרים המובילים ברכש',
                hint: 'כל המוצרים בתקופה לפי סכום, עם מחיר מינ׳, מקס׳ ונוכחי ו-% שינוי',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'top_products' });
                  return analysisApi.exportTopProducts(buildScopedParams());
                },
              }, {
                key: 'top_products_orders',
                label: 'המוצרים המובילים ברכש - כולל הזמנות',
                hint: 'אותו דוח, עם גיליון נוסף: כל הזמנה של כל מוצר ברשימה',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'top_products_orders' });
                  return analysisApi.exportTopProductsWithOrders(buildScopedParams());
                },
              }] : []),
              {
                key: 'classifications',
                label: 'רכש לפי סיווג פריטים',
                run: () => {
                  // Wire value, not a label — see EVENTS in utils/analytics.js.
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'categories' });
                  return analysisApi.exportClassifications(buildParams());
                },
              },
              {
                key: 'items',
                label: 'רכש לפי מוצרים',
                hint: 'שורה לכל שורת רכש, כל הספקים. קובץ גדול, ההכנה עשויה לקחת מספר שניות',
                run: () => {
                  track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'all_items' });
                  return analysisApi.exportAllItems({
                    ...buildParams(),
                    ...(nameFilter || topProductFilter
                      ? { supplier_ids: filtered.map(s => s.id).join(',') } : {}),
                  });
                },
              },
            ]}
          />
        )}
        <div className="page-menu">
          <button
            className="page-menu-trigger"
            aria-label="פעולות נוספות"
            aria-expanded={pageMenuOpen}
            onClick={() => setPageMenuOpen(o => !o)}
          >⋮</button>
          {pageMenuOpen && (
            <>
              <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
              <div className="page-menu-panel">
                <button className="page-menu-item"
                  disabled={loading}
                  onClick={() => { setPageMenuOpen(false); refreshAll(); }}
                  title="טען מחדש מהמסד נתונים">
                  <Icon name="recurring" size={15} /> רענן
                </button>
                {isAdmin && (
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); setReportOpen(true); }}
                    title="דוח שינויי מחיר לכל הספקים בתקופה שנבחרה">
                    <Icon name="trend" size={15} /> דוח שינויי מחיר
                  </button>
                )}
                {/* Also reachable from the by-classification card, but that only
                    exists in the classification view — this keeps it available from
                    the supplier view too. A button, not a Link, to match the
                    siblings' styling. */}
                {canEdit && (
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); navigate('/item-classifications'); }}
                    title="סיווג מוצרים לסיווגים">
                    <Icon name="folder" size={15} /> ניהול סיווג פריטים
                  </button>
                )}
                {/* Named for where it actually LANDS. `?revenue=1` opens the
                    revenue section and scrolls to it (see
                    CategoriesManagementPage), so this is a revenue shortcut,
                    not a targets one — and it must not reuse "ניהול יעדי
                    תקציב", which is the label of the targets item just above
                    it (and of WeeklyBudgetBreakdown's own ⋮ item further down),
                    both pointing at ?targets=1. Two identical labels going to
                    two different places is the bug this wording avoids. Same
                    budget_module_enabled gate as that card: no point offering
                    a shortcut into a module this restaurant does not have on. */}
                {/* The plain page, and labelled with that page's OWN title
                    ("ניהול קטגוריות") rather than a section of it — same rule
                    as the item below: name where it lands. canEdit is admin +
                    manager (roles.js#canManage's client mirror), and the
                    budget-module gate is the one the whole page uses: the
                    screen this opens is entirely behind that flag. */}
                {canEdit && budgetModuleEnabled && (
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); navigate('/categories-management'); }}
                    title="הגדרת קטגוריות, יעדי תקציב ושיוכים">
                    <Icon name="tag" size={15} /> ניהול קטגוריות
                  </button>
                )}
                {/* Straight to the targets sheet (owner, 2026-09-21). Same label,
                    icon and destination as WeeklyBudgetBreakdown's own ⋮ item,
                    so the two doors agree; this one is reachable without
                    scrolling to the card, and while the card is loading or
                    has failed. `?targets=1` expands the targets sheet and
                    collapses the rest (CategoriesManagementPage's
                    arrivedForTargets). */}
                {canEdit && budgetModuleEnabled && (
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); navigate('/categories-management?targets=1'); }}
                    title="הגדרת יעדי תקציב לכל קטגוריה">
                    <Icon name="coins" size={15} /> ניהול יעדי תקציב
                  </button>
                )}
                {canEdit && budgetModuleEnabled && (
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); navigate('/categories-management?revenue=1'); }}
                    title="הזנת פדיון מדוח Z, בעמוד ניהול הקטגוריות">
                    <Icon name="coins" size={15} /> הזנת הכנסות
                  </button>
                )}
                <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
              </div>
            </>
          )}
        </div>
        </div>
      </div>

      {/* Which ALTITUDE the page is read at — one restaurant, or a whole chain.
          Only rendered for the few users who can see a network at all.

          It used to be a .card carrying a labelled <select>, and it read as
          unrelated to the page for two structural reasons, not cosmetic ones.
          First, .card on this page means "here is a piece of your data", and
          this control is the PARENT of every card below it — it decides which
          cards exist at all — so wearing a card filed it one level beneath its
          own output. Second, it sat BELOW the filter bar it rewrites, so
          flipping it changed the row ABOVE the thing you had just tapped.

          Now it is chrome, not content, and it says so the only way this
          borderless system can: content is LIGHTER than the page (white cards),
          so chrome is DARKER. No border, no shadow, no heading needed, and the
          1.13:1 page-to-card discipline is untouched.

          The selected segment is painted rather than slid: an absolutely
          positioned thumb is the classic RTL bug (translateX computed for LTR
          slides the wrong way), and there is nothing to gain from the animation
          that is worth that risk. */}
      {networks.length > 0 && (() => {
        // A restaurant can belong to MORE THAN ONE chain — that is the normal
        // shape in production, not an edge case — so the control has two forms
        // and the threshold is measured, not guessed.
        //
        // With one chain the pill is two segments and both labels fit. With two
        // chains it is three, and at 375px each segment gets 114px while
        // "רשת אהרוניס פרייד צ׳׳יקן" needs 151 — it truncated to
        // "רשת אהרוניס פ…", which is worse than useless when two chains share a
        // prefix: the user cannot tell the options apart, and picking the wrong
        // one silently rewrites the whole page. So past two segments the pill
        // becomes ONE button naming the current scope, opening the same
        // .page-menu-panel list the ⋮ above it uses. Same 40px height either
        // way, so nothing below moves between the two forms.
        const scopeName = selectedNetworkId
          ? `רשת ${networks.find(n => n.id === selectedNetworkId)?.name ?? ''}`
          : 'מסעדה בודדת';
        const subtitle = selectedNetworkId
          ? `${networks.find(n => n.id === selectedNetworkId)?.name ?? ''} · דוח מאוחד לכל הסניפים`
          : (activeRestaurantName || 'מסעדה בודדת');
        const options = [{ id: null, label: 'מסעדה בודדת', note: activeRestaurantName },
                         ...networks.map(n => ({ id: n.id, label: `רשת ${n.name}`, note: 'דוח מאוחד' }))];
        return (
          <div style={{ marginBottom: 12 }}>
            {networks.length === 1 ? (
              <div className="scope-pill" role="radiogroup" aria-label="תצוגה">
                {[{ id: null, label: 'מסעדה בודדת' }, { id: networks[0].id, label: 'כל הרשת' }].map(seg => (
                  <button
                    key={seg.id ?? 'single'}
                    type="button"
                    role="radio"
                    aria-checked={selectedNetworkId === seg.id}
                    className={`scope-seg${selectedNetworkId === seg.id ? ' is-active' : ''}`}
                    onClick={() => setScope(seg.id)}
                  >
                    {seg.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="page-menu" style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="scope-trigger"
                  aria-haspopup="menu"
                  aria-expanded={scopeMenuOpen}
                  aria-label={`תצוגה: ${scopeName}. שינוי`}
                  onClick={() => setScopeMenuOpen(o => !o)}
                >
                  <span className="scope-trigger-label">{scopeName}</span>
                  <Icon name="chevron" size={16} />
                </button>
                {scopeMenuOpen && (
                  <>
                    <div className="menu-backdrop" onClick={() => setScopeMenuOpen(false)} />
                    <div className="page-menu-panel scope-menu-panel">
                      {options.map(o => (
                        <button
                          key={o.id ?? 'single'}
                          type="button"
                          className="page-menu-item"
                          onClick={() => { setScope(o.id); setScopeMenuOpen(false); }}
                        >
                          <span style={{ width: 15, flexShrink: 0 }}>
                            {selectedNetworkId === o.id && <Icon name="check" size={15} />}
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {o.label}
                            </span>
                            {o.note && (
                              <span style={{ display: 'block', fontSize: 11.5, color: '#94a3b8' }}>{o.note}</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* Identity lives here, not in the control — which is what lets
                "כל הרשת" work as a two-word label. In single view it echoes the
                navbar's tenant chip verbatim, teaching the relationship between
                the two controls without a word of explanation. It also absorbs
                the grey sentence this control used to carry: that sentence only
                ever existed to promise the date filter still applied, a doubt
                created purely by sitting below it. */}
            <div style={{ fontSize: 12.5, color: '#94a3b8', marginTop: 6 }}>{subtitle}</div>
          </div>
        );
      })()}

      {/* Sticky filter bar — extracted to AnalysisFilterBar (same UX as
          /suppliers and /documents per FILTER_AND_DATE_RULES.md). */}
      <AnalysisFilterBar
        nameFilter={nameFilter}             setNameFilter={setNameFilter}
        topProductFilter={topProductFilter} setTopProductFilter={setTopProductFilter}
        dateFrom={dateFrom}                 dateTo={dateTo}
        setDateRange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        openPeriod={rangeIsOpen}
        hasFilters={Boolean(hasFilters)}
        onClearAll={clearFilters}
        resultCount={filtered.length}
        // A single restaurant has nothing for a supplier/product search to
        // scope down — that's a network-view action (narrowing the
        // consolidated table). Single-restaurant view keeps only the date
        // range; switching into a network restores the full bar.
        hideSupplierFilters={!selectedNetworkId}
      />

      {/* By-supplier and by-classification used to be exclusive tabs, so seeing both
          meant clicking between them and holding one in your head. They are
          two views of the same money — the classification card answers "where is it
          going", the supplier card "who am I paying" — so both render, always.
          The network view keeps its own structure. */}
      {networkPending ? (
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      ) : chainUnresolved ? (
        <InlineLoadError message="לא ניתן לטעון את נתוני הרשת" onRetry={() => setNetworksRetry(n => n + 1)} />
      ) : selectedNetworkId ? (
        <NetworkAnalysis networkId={selectedNetworkId} dateFrom={dateFrom} dateTo={dateTo} isAdmin={isAdmin} canViewDiscounts={canViewDiscounts} nameFilter={nameFilter} topProductFilter={topProductFilter} onHiddenHq={setNetworkHidden} onExportable={setNetworkExportNames} />
      ) : (
      <>
      {/* The one high-level number, above the cards that break it down.
          Deliberately ONE metric and not a strip of four: the other candidates
          measured near-nothing on real data (open credits ₪53, unapproved ₪0
          across every tenant), and a tile that always reads ₪0 is worse than
          no tile. NET only (owner) — no gross twin — so the whole card, the
          figure and the delta beneath it, speaks one currency.

          The delta is what makes the figure judgeable: a total on its own
          cannot say whether it is good, and the budget card below only answers
          that for lines someone has already budgeted. */}
      {!loadError && (
        <div className="card total-band">
          {/* Same title grammar as the cards below it (רכש לפי ספקים et al),
              and in the same PLACE — its own header line at the top start
              corner, on desktop exactly as on mobile. */}
          {/* At desktop this card becomes a horizontal BAND: title, figure and
              caveat on one axis instead of stacked. It was 129px of card
              chrome around a single number across 1288px — the clearest case
              on the page of a container not earning its keep. The card itself
              stays (removing it would make the page's one ANSWER a bare block
              among five bordered breakdowns, and .card is the only structural
              affordance this borderless system has) but its contents stop
              behaving like a phone. */}
          <div className="total-band__head">
            <h3 className="total-band__title" style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)' }}>
              סה״כ רכש
            </h3>
            {/* Same ⋮ idiom as every other card on this page. ONE item: this
                card has no report to open and nothing to configure — what a
                reader actually asks of a single headline number is what went
                into it. */}
            <div className="page-menu" style={{ position: 'relative' }}>
              <button
                type="button"
                className="page-menu-trigger"
                aria-label="פעולות נוספות"
                aria-expanded={totalMenuOpen}
                onClick={() => setTotalMenuOpen(o => !o)}
              >⋮</button>
              {totalMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setTotalMenuOpen(false)} />
                  <div className="page-menu-panel" style={{ minWidth: 300, padding: 14, cursor: 'default' }}>
                    <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>איך מחושב</div>
                    <p style={{ margin: 0, fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                      סכום כל שורות המסמכים המאושרים בטווח, ללא מע״מ. זיכויים
                      מופחתים, וטיוטות אינן נספרות עד לאישורן.
                    </p>
                    {rangeIsOpen && (
                      <p style={{ margin: '8px 0 0', fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                        הטווח כולל את החודש הנוכחי, ולכן התקופה טרם נסגרה
                        חשבונאית והמספרים עשויים להשתנות — הם יכולים רק לעלות,
                        ככל שמסמכים נוספים יאושרו.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {/* NOWRAP on the twins (owner): on a phone they must sit side by
              side — stacking them doubled the band's height for one fact.
              Each column flexes and the caption wraps under its number when
              tight, but the columns never stack. */}
          {loading ? <span className="stat-skeleton" aria-hidden="true" /> : (
            // The gross twin is gone (owner): ONE figure, without VAT, and the
            // delta below it judges that same net number against the previous
            // period's net total — never a gross figure the card no longer
            // shows at all.
            <div className="total-band__body">
              {/* formatCurrency, NOT formatCurrencyWhole. Both figures labelled
                  סה״כ רכש on this page come from the same
                  buildSupplierSlices(filtered).total, so they are the same
                  money — but rendered whole here and to the agora in the donut
                  footer they read as 7,166 against 7,165.73, which looks like a
                  disagreement rather than a rounding choice. This is the figure
                  a reader ties the page out against, and formatCurrencyWhole's
                  own contract reserves it for numbers being COMPARED rather
                  than reconciled. */}
              <div className="total-band__figure" style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(spend.net)}
                <span style={{ fontSize: 12, fontWeight: 400, color: '#8a93a0' }}> (ללא מע״מ)</span>
              </div>
              {/* Number first, then a label that SAYS the direction (פחות/יותר)
                  instead of leaving the arrow to carry it alone; the native
                  title names the exact previous window on hover (desktop-only
                  by construction). */}
              {/* The delta's replacement, not an addition: an open range gets
                  the reason there is no comparison, and the size of what is
                  still missing. Grey, never amber - nothing here is wrong or
                  actionable, the month simply is not over. */}
              {rangeIsOpen && (
                <div style={{ fontSize: 12, color: '#8a93a0', marginTop: 4, lineHeight: 1.5 }}>
                  {/* The same amber calendar the period picker is wearing, so
                      the mark in the bar and the sentence explaining it are
                      visibly the same fact rather than two coincidences. */}
                  <Icon name="calendar" size={12} style={{ color: '#d97706', marginInlineEnd: 5, verticalAlign: -1 }} />
                  התקופה טרם נסגרה חשבונאית
                  {openInfo?.pending_docs > 0 && (
                    <> · {openInfo.pending_docs} מסמכים ממתינים לאישור</>
                  )}
                </div>
              )}
              {deltaPct !== null && (() => {
                const prev = previousRange(dateFrom, dateTo);
                const n = parseFloat(deltaPct);
                const label = n < 0 ? 'פחות מהתקופה הקודמת'
                  : n > 0 ? 'יותר מהתקופה הקודמת' : 'מול התקופה הקודמת';
                return (
                  <div className="page-menu" style={{ position: 'relative', marginTop: 4 }}>
                    {/* A BUTTON, not a div with a title: the native tooltip it
                        used to carry needs a mouse, and this page is read on a
                        phone. Same panel idiom as the ⋮ menus so there is one
                        disclosure grammar on the page. */}
                    <button
                      type="button"
                      className="total-band__delta"
                      aria-expanded={prevTipOpen}
                      onClick={() => setPrevTipOpen(o => !o)}
                    >
                      {renderChangePct(deltaPct)}
                      <span style={{ color: '#8a93a0' }}>{label}</span>
                      <Icon name="info" size={12} style={{ color: '#94a3b8' }} />
                    </button>
                    {prevTipOpen && (
                      <>
                        <div className="menu-backdrop" onClick={() => setPrevTipOpen(false)} />
                        <div className="page-menu-panel" style={{ minWidth: 260, padding: 14, cursor: 'default' }}>
                          <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>מול איזו תקופה</div>
                          <div style={{ display: 'grid', gap: 6, fontSize: 12.5, color: '#475569' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span>התקופה הנוכחית</span>
                              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {formatDateShort(dateFrom)}–{formatDateShort(dateTo)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                              <span>התקופה הקודמת</span>
                              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {prev ? `${formatDateShort(prev.from)}–${formatDateShort(prev.to)}` : '—'}
                              </span>
                            </div>
                            {prevSpend?.total_amount != null && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid #eef2f6', paddingTop: 6 }}>
                                <span>רכש בתקופה הקודמת</span>
                                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {formatCurrency(prevSpend.total_amount)}
                                </span>
                              </div>
                            )}
                          </div>
                          {/* The rule the dates come from, said once. Without it
                              "1–26 ביולי" against a full August looks like a bug
                              rather than the like-for-like it is. */}
                          <p style={{ margin: '8px 0 0', fontSize: 11.5, color: '#8a93a0', lineHeight: 1.55 }}>
                            ההשוואה היא לאותו מספר ימים שחלפו, כדי שחודש מלא לא
                            יימדד מול חודש שרק התחיל.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* DOM order is 2, 5, 3, 4 — reordered from 2, 3, 4, 5, and in the DOM
          rather than with CSS `order`, so reading order, tab order and visual
          order can never disagree.
          The reorder stands on its own merits: רכש לפי ספקים and רכש לפי סיווג
          פריטים are two cuts of ONE question ("where did the money go"), and
          the two budget cards are two cuts of another. The flag-gated pair used
          to be wedged between the always-present pair. Grouping them is better
          on a phone too — and it means the pair that shares a grid row is the
          pair that is ALWAYS on screen, so a module-off restaurant can never
          see a half-empty row.

          Two columns only from 1200px. Below that nothing changes, which keeps
          the squeeze off small laptops and iPad landscape entirely.

          minmax(0, 1fr), never 1fr: the default minimum track is min-content,
          and these cards have real min-content widths (a fixed-size donut, a
          five-column row grid). A plain 1fr refuses to shrink below them and
          overflows the page instead of wrapping. */}
      <div className="analysis-grid">
        <Suspense fallback={
          <div className="card">
            <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
          </div>
        }>
          <SupplierDonutCard
            suppliers={filtered}
            loading={loading}
            loadError={loadError}
            onRetry={fetchSuppliers}
            dateFrom={dateFrom}
            dateTo={dateTo}
            // Only the name/product filters — NOT the period, which is always set
            // and is already named in the empty-state message either way.
            filterActive={Boolean(nameFilter || topProductFilter)}
          />
        </Suspense>

        <ClassificationAnalysis
          params={buildScopedParams()}
          // Same signal the donut gets: only the name/product filters, not the
          // period — which is always set and already named in the empty-state copy.
          filterActive={Boolean(nameFilter || topProductFilter)}
        />

        {/* המוצרים המובילים ברכש — a third cut of "where did the money go",
            so it follows that pair, ahead of the budget lenses. Full width,
            like the budget cards: behind its own flag, so in a column it would
            leave a half-empty grid row for every restaurant that has it off.
            Hidden in the network view (it is per restaurant). Same scoped
            params as the classification card: the page's supplier filter
            narrows it too. */}
        {topProductsEnabled && (
          <div className="analysis-grid-full">
            <TopProductsCard
              params={buildScopedParams()}
              filterActive={Boolean(nameFilter || topProductFilter)}
            />
          </div>
        )}

        {/* Revenue vs. expenses first, budget target second (owner). The pair
            reads as one thought: "how does spend compare to what came in", then
            "am I within budget" as the second lens on the same money.
            The budget lens is פירוט שבועי/חודשי מול יעדי תקציב. It replaced
            רכש מול יעדי תקציב (RestaurantTargetPanel, deleted) after an A/B
            test on this page, taking over that card's amber "שייך לקטגוריות"
            call to action, and it has no flag of its own any more: the
            budget module is its only gate.
            Both span the full width rather than taking a column. הכנסות מול
            הוצאות earned 1336px — its category rows are a five-column grid with
            a comparison bar — and halving it would trade a working instrument
            for less scrolling. Both are hidden in the network view (chain
            budgets do not exist yet) and behind budget_module_enabled. */}
        {budgetModuleEnabled && (
          <>
            {/* Lean here — the full coverage story is /categories-management's. */}
            <div className="analysis-grid-full">
              <RevenueVsBudgetPanel dateFrom={dateFrom} dateTo={dateTo} />
            </div>
            <div className="analysis-grid-full">
              <WeeklyBudgetBreakdown dateFrom={dateFrom} dateTo={dateTo} />
            </div>
          </>
        )}
      </div>
      </>
      )}

      {reportOpen && isAdmin && (
        <PriceChangeReportModal onClose={() => setReportOpen(false)} />
      )}

    </div>
  );
}
